import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  TablePagination,
  useTheme,
  alpha,
  Container
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Notes as NotesIcon,
  Scale as ScaleIcon,
  Assessment as VarianceIcon,
  Error as ErrorIcon,
  LocationOn as LocationOnIcon,
  Delete as DeleteIcon,
  Edit as EditNoteIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  ExpandLess as ExpandLessIcon,
  DateRange as DateRangeIcon,
  Inventory2 as LotIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import { useNavigate } from 'react-router-dom';
import InventoryTable from '../../inventory/inventory-components/shared/InventoryTable';

const inventoryTypeConfigs = {
  cap: {
    displayName: 'Caps', singularName: 'Cap', type: 'caps',
    itemIdField: 'cap_id', inventoryTable: 'caps_inventory',
    itemsTable: 'caps_inventory_items', transactionsTable: 'caps_transactions',
    hasUnitOfMeasure: false, unit: 'units', lastUpdatedField: 'last_updated'
  },
  bottle: {
    displayName: 'Bottles', singularName: 'Bottle', type: 'bottles',
    itemIdField: 'bottle_id', inventoryTable: 'bottle_inventory',
    itemsTable: 'bottle_inventory_items', transactionsTable: 'bottle_transactions',
    hasUnitOfMeasure: false, unit: 'units', lastUpdatedField: 'last_updated'
  },
  label: {
    displayName: 'Labels', singularName: 'Label', type: 'labels',
    itemIdField: 'label_id', inventoryTable: 'label_inventory',
    itemsTable: 'label_inventory_items', transactionsTable: 'label_transactions',
    hasUnitOfMeasure: false, unit: 'units', lastUpdatedField: 'last_updated'
  },
  supply: {
    displayName: 'Supplies', singularName: 'Supply', type: 'supplies',
    itemIdField: 'supply_id', inventoryTable: 'supply_inventory',
    itemsTable: 'supply_inventory_items', transactionsTable: 'supply_transactions',
    hasUnitOfMeasure: false, unit: 'units', lastUpdatedField: 'last_updated'
  }
};

const ProductionLineItems = ({
  lineProduction, 
  products, 
  showSnackbar, 
  userCompanyId, 
  onUpdate, 
  user 
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // All useState hooks must be called before any conditional returns
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [inventoryData, setInventoryData] = useState([]);
  const [inventoryItemsData, setInventoryItemsData] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [expandedInventoryRows, setExpandedInventoryRows] = useState(new Set());
  const [productionLocationName, setProductionLocationName] = useState('');
  const [productionNotes, setProductionNotes] = useState('');
  const [savingProductionNotes, setSavingProductionNotes] = useState(false);
  const [showSavedNotesDialog, setShowSavedNotesDialog] = useState(false);
  const [lineNotes, setLineNotes] = useState([]);
  const [loadingLineNotes, setLoadingLineNotes] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [savingEditNote, setSavingEditNote] = useState(false);
  const [conditionMenuAnchor, setConditionMenuAnchor] = useState(null);
  const [selectedItemForCondition, setSelectedItemForCondition] = useState(null);
  const [notesEditAnchor, setNotesEditAnchor] = useState(null);
  const [selectedItemForNotes, setSelectedItemForNotes] = useState(null);
  const [quickEditNotes, setQuickEditNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showLotBatchDialog, setShowLotBatchDialog] = useState(false);
  const [selectedItemForLotBatch, setSelectedItemForLotBatch] = useState(null);
  const [availableLots, setAvailableLots] = useState([]);
  const [selectedLots, setSelectedLots] = useState([]);
  const [remainingQuantityNeeded, setRemainingQuantityNeeded] = useState(0);
  const [savingLotSelections, setSavingLotSelections] = useState(false);
  const [lotSearchTerm, setLotSearchTerm] = useState('');
  const [lotSummaries, setLotSummaries] = useState({});
  const [inventoryPage, setInventoryPage] = useState(0);
  const [inventoryRowsPerPage, setInventoryRowsPerPage] = useState(3);
  // Accordion state management with localStorage persistence
  const [accordionStates, setAccordionStates] = useState(() => {
    try {
      const saved = localStorage.getItem(`productionLineItems_accordions_${lineProduction?.id}`);
      return saved ? JSON.parse(saved) : {
        progressOverview: false,
        inventoryOverview: false, // Default to collapsed
        itemTypes: {} // Dynamic object for item type accordions
      };
    } catch {
      return {
        progressOverview: false,
        inventoryOverview: false, // Default to collapsed
        itemTypes: {} // Dynamic object for item type accordions
      };
    }
  });

  // useEffect hooks - must be before permission check
  useEffect(() => {
    if (lineProduction?.id) {
      loadLineItems();
      loadLineNotes();
    }
  }, [lineProduction?.id]);

  // Load inventory data after items are loaded
  useEffect(() => {
    if (lineProduction?.id && items.length > 0) {
      loadInventoryData();
    }
  }, [lineProduction?.id, items]);

  useEffect(() => {
    if (items.length > 0) {
      loadAllLotSummaries();
    }
  }, [items]);

  // Permission check - must be after all hooks
  if (!hasPermission('production.line.assembly.items.view', 'read')) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: 'calc(100vh - 200px)',
        textAlign: 'center',
        width: '100%',
        px: 3,
        py: 8
      }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <InventoryIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Production Line Items Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Production Line Items. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
      </Box>
    );
  }

  // Check edit permissions  
  const canEdit = hasPermission('production.line.assembly.items.edit', 'write');
  const canDelete = hasPermission('production.line.assembly.items.delete', 'delete');

  // Function declarations - using function keyword for hoisting
  async function loadLineItems(showFeedback = false) {
    if (showFeedback) setLoading(true);
    
    try {
      // Load basic line items first
      const { data: itemsData, error: itemsError } = await supabase
        .from('production_line_items')
        .select('*')
        .eq('line_production_id', lineProduction.id);

      if (itemsError) throw itemsError;

      // Now load the related item details for each item based on item_type
      const enrichedItems = await Promise.all((itemsData || []).map(async (item) => {
        let itemDetails = null;
        
        try {
          if (item.item_type === 'bottle') {
            const { data: bottleData } = await supabase
              .from('bottles')
              .select('id, name, sku, capacity, capacity_unit, color, material')
              .eq('id', item.item_id)
              .single();
            itemDetails = { bottle: bottleData };
          } else if (item.item_type === 'cap') {
            const { data: capData } = await supabase
              .from('caps')
              .select('id, name, sku, closure_type, color, material')
              .eq('id', item.item_id)
              .single();
            itemDetails = { cap: capData };
          } else if (item.item_type === 'label') {
            const { data: labelData } = await supabase
              .from('labels')
              .select('id, name, sku, material, finish')
              .eq('id', item.item_id)
              .single();
            itemDetails = { label: labelData };
          } else if (item.item_type === 'supply') {
            const { data: supplyData } = await supabase
              .from('supplies')
              .select('id, name, sku, unit, attribute_1_label, attribute_1_value')
              .eq('id', item.item_id)
              .single();
            itemDetails = { supply: supplyData };
          }
        } catch (detailError) {
          console.error(`Error loading ${item.item_type} details:`, detailError);
        }
        
        return { ...item, ...itemDetails };
      }));
      
      setItems(enrichedItems);
      
      if (showFeedback) {
        const count = enrichedItems.length;
        showSnackbar(`Data refreshed! Loaded ${count} item${count !== 1 ? 's' : ''}.`, 'success');
      }
      
    } catch (error) {
      console.error('Error loading line items:', error);
      if (showFeedback) {
        showSnackbar('Error loading items: ' + error.message, 'error');
      }
    } finally {
      if (showFeedback) setLoading(false);
    }
  }

  async function loadInventoryData() {
    try {
      setLoadingInventory(true);
      
      // Get location name for display if line has a production location ID
      if (lineProduction?.production_location) {
        const isLocationId = lineProduction.production_location.includes('-') && lineProduction.production_location.length > 20;
        
        if (isLocationId) {
          const { data: locationData, error: locationError } = await supabase
            .from('locations')
            .select('name')
            .eq('id', lineProduction.production_location)
            .eq('company_id', userCompanyId)
            .single();
          
          if (!locationError && locationData) {
            setProductionLocationName(locationData.name);
          } else {
            setProductionLocationName(lineProduction.production_location);
          }
        } else {
          setProductionLocationName(lineProduction.production_location);
        }
      }
      
      // Extract item IDs by type from the production line items
      const bottleIds = items.filter(item => item.item_type === 'bottle').map(item => item.item_id);
      const capIds = items.filter(item => item.item_type === 'cap').map(item => item.item_id);
      const labelIds = items.filter(item => item.item_type === 'label').map(item => item.item_id);
      const supplyIds = items.filter(item => item.item_type === 'supply').map(item => item.item_id);
      
      // Only load inventory if there are items of that type
      let bottlesData = [];
      if (bottleIds.length > 0) {
        const { data, error: bottlesError } = await supabase
          .from('bottle_inventory')
          .select(`
            *,
            bottle:bottle_id(
              id,
              name,
              sku,
              capacity,
              capacity_unit,
              color,
              material
            ),
            location:location_id(
              id,
              name
            )
          `)
          .eq('company_id', userCompanyId)
          .in('bottle_id', bottleIds)
          .order('current_quantity', { ascending: true });

        if (bottlesError) throw bottlesError;
        bottlesData = data || [];
      }

      // Load caps inventory only for caps in production line
      let capsData = [];
      if (capIds.length > 0) {
        const { data, error: capsError } = await supabase
          .from('caps_inventory')
          .select(`
            *,
            cap:cap_id(
              id,
              name,
              sku,
              closure_type,
              color,
              material
            ),
            location:location_id(
              id,
              name
            )
          `)
          .eq('company_id', userCompanyId)
          .in('cap_id', capIds)
          .order('current_quantity', { ascending: true });

        if (capsError) throw capsError;
        capsData = data || [];
      }

      // Load labels inventory only for labels in production line
      let labelsData = [];
      if (labelIds.length > 0) {
        const { data, error: labelsError } = await supabase
          .from('label_inventory')
          .select(`
            *,
            label:label_id(
              id,
              name,
              sku,
              material,
              finish
            ),
            location:location_id(
              id,
              name
            )
          `)
          .eq('company_id', userCompanyId)
          .in('label_id', labelIds)
          .order('current_quantity', { ascending: true });

        if (labelsError) throw labelsError;
        labelsData = data || [];
      }

      // Load supplies inventory only for supplies in production line
      let suppliesData = [];
      if (supplyIds.length > 0) {
        const { data, error: suppliesError } = await supabase
          .from('supply_inventory')
          .select(`
            *,
            supply:supply_id(
              id,
              name,
              sku,
              unit,
              attribute_1_label,
              attribute_1_value
            ),
            location:location_id(
              id,
              name
            )
          `)
          .eq('company_id', userCompanyId)
          .in('supply_id', supplyIds)
          .order('current_quantity', { ascending: true });

        if (suppliesError) throw suppliesError;
        suppliesData = data || [];
      }
      
      // Load detailed inventory items data (lot/batch level) only for production line items
      let bottleItemsData = [];
      if (bottleIds.length > 0) {
        const { data, error: bottleItemsError } = await supabase
          .from('bottle_inventory_items')
          .select(`
            *,
            bottle:bottle_id(
              id,
              name,
              sku,
              capacity,
              capacity_unit,
              color,
              material
            ),
            location:location_id(
              id,
              name
            )
          `)
          .eq('company_id', userCompanyId)
          .eq('is_active', true)
          .in('bottle_id', bottleIds)
          .order('created_at', { ascending: true });

        if (bottleItemsError) throw bottleItemsError;
        bottleItemsData = data || [];
      }

      let capItemsData = [];
      if (capIds.length > 0) {
        const { data, error: capItemsError } = await supabase
          .from('caps_inventory_items')
          .select(`
            *,
            cap:cap_id(
              id,
              name,
              sku,
              closure_type,
              color,
              material
            ),
            location:location_id(
              id,
              name
            )
          `)
          .eq('company_id', userCompanyId)
          .eq('is_active', true)
          .in('cap_id', capIds)
          .order('created_at', { ascending: true });

        if (capItemsError) throw capItemsError;
        capItemsData = data || [];
      }

      let labelItemsData = [];
      if (labelIds.length > 0) {
        const { data, error: labelItemsError } = await supabase
          .from('label_inventory_items')
          .select(`
            *,
            label:label_id(
              id,
              name,
              sku,
              material,
              finish
            ),
            location:location_id(
              id,
              name
            )
          `)
          .eq('company_id', userCompanyId)
          .eq('is_active', true)
          .in('label_id', labelIds)
          .order('created_at', { ascending: true });

        if (labelItemsError) throw labelItemsError;
        labelItemsData = data || [];
      }

      let supplyItemsData = [];
      if (supplyIds.length > 0) {
        const { data, error: supplyItemsError } = await supabase
          .from('supply_inventory_items')
          .select(`
            *,
            supply:supply_id(
              id,
              name,
              sku,
              unit,
              attribute_1_label,
              attribute_1_value
            ),
            location:location_id(
              id,
              name
            )
          `)
          .eq('company_id', userCompanyId)
          .eq('is_active', true)
          .in('supply_id', supplyIds)
          .order('created_at', { ascending: true });

        if (supplyItemsError) throw supplyItemsError;
        supplyItemsData = data || [];
      }

      // Combine all inventory data with type indicators (caps first, then bottles, labels, supplies)
      const combinedInventory = [
        ...(capsData || []).map(item => ({ ...item, inventory_type: 'cap' })),
        ...(bottlesData || []).map(item => ({ ...item, inventory_type: 'bottle' })),
        ...(labelsData || []).map(item => ({ ...item, inventory_type: 'label' })),
        ...(suppliesData || []).map(item => ({ ...item, inventory_type: 'supply' }))
      ];

      const combinedInventoryItems = [
        ...(capItemsData || []).map(item => ({ ...item, inventory_type: 'cap' })),
        ...(bottleItemsData || []).map(item => ({ ...item, inventory_type: 'bottle' })),
        ...(labelItemsData || []).map(item => ({ ...item, inventory_type: 'label' })),
        ...(supplyItemsData || []).map(item => ({ ...item, inventory_type: 'supply' }))
      ];
      
      setInventoryData(combinedInventory);
      setInventoryItemsData(combinedInventoryItems);
    } catch (error) {
      console.error('Error loading inventory data:', error);
    } finally {
      setLoadingInventory(false);
    }
  }

  async function loadLineNotes() {
    if (!lineProduction?.id) return;
    
    try {
      setLoadingLineNotes(true);
      
      const { data, error } = await supabase
        .from('production_line_notes')
        .select(`
          *
        `)
        .eq('line_production_id', lineProduction.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLineNotes(data || []);
    } catch (error) {
      console.error('Error loading line notes:', error);
      showSnackbar('Error loading line notes: ' + error.message, 'error');
    } finally {
      setLoadingLineNotes(false);
    }
  }

  async function loadAllLotSummaries() {
    try {
      const summaries = {};
      for (const item of items) {
        const selections = await loadExistingLotSelections(item.id);
        if (selections && selections.length > 0) {
          const totalQuantity = selections.reduce((sum, sel) => sum + (sel.quantity_used || 0), 0);
          const lotCount = selections.length;
          
          summaries[item.id] = {
            totalQuantity,
            lotCount,
            lots: selections
          };
        }
      }
      setLotSummaries(summaries);
    } catch (error) {
      console.error('Error loading lot summaries:', error);
    }
  }

  const deleteLineNote = async (noteId) => {
    // Check permissions before proceeding
    if (!canDelete) {
      showSnackbar('You do not have permission to delete notes.', 'error');
      return;
    }

    try {
      setDeletingNoteId(noteId);
      
      const { error } = await supabase
        .from('production_line_notes')
        .delete()
        .eq('id', noteId);
      
      if (error) throw error;
      
      await loadLineNotes();
      showSnackbar('Note deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting note:', error);
      showSnackbar('Error deleting note: ' + error.message, 'error');
    } finally {
      setDeletingNoteId(null);
    }
  };

  const startEditNote = (note) => {
    setEditingNote(note);
    setEditNoteContent(note.note_content);
  };

  const cancelEditNote = () => {
    setEditingNote(null);
    setEditNoteContent('');
  };

  const saveEditNote = async () => {
    if (!editingNote || !editNoteContent.trim()) return;
    
    // Check permissions before proceeding
    if (!canEdit) {
      showSnackbar('You do not have permission to edit notes.', 'error');
      cancelEditNote();
      return;
    }
    
    try {
      setSavingEditNote(true);
      
      const { error } = await supabase
        .from('production_line_notes')
        .update({
          note_content: editNoteContent.trim()
        })
        .eq('id', editingNote.id);
      
      if (error) throw error;
      
      await loadLineNotes();
      showSnackbar('Note updated successfully!', 'success');
      cancelEditNote();
    } catch (error) {
      console.error('Error updating note:', error);
      showSnackbar('Error updating note: ' + error.message, 'error');
    } finally {
      setSavingEditNote(false);
    }
  };

  const deleteAllLineNotes = async () => {
    // Check permissions before proceeding
    if (!canDelete) {
      showSnackbar('You do not have permission to delete notes.', 'error');
      return;
    }

    try {
      setLoadingLineNotes(true);
      
      const { error } = await supabase
        .from('production_line_notes')
        .delete()
        .eq('line_production_id', lineProduction.id);
      
      if (error) throw error;
      
      await loadLineNotes();
      showSnackbar('All notes deleted successfully!', 'success');
      setShowSavedNotesDialog(false);
    } catch (error) {
      console.error('Error deleting all notes:', error);
      showSnackbar('Error deleting all notes: ' + error.message, 'error');
    } finally {
      setLoadingLineNotes(false);
    }
  };

  const generateLineItems = async () => {
    // Check permissions before proceeding
    if (!canEdit) {
      showSnackbar('You do not have permission to generate line items.', 'error');
      return;
    }

    setLoading(true);
    try {
      // Delete existing line items
      await supabase
        .from('production_line_items')
        .delete()
        .eq('line_production_id', lineProduction.id);

      // Get the actual planned quantity from line production
      const totalUnitsNeeded = lineProduction?.planned_quantity || lineProduction?.units_to_make || 0;
      
      if (totalUnitsNeeded === 0) {
        showSnackbar('No planned quantity set for this line production', 'warning');
        setLoading(false);
        return;
      }

      // Get template consumption data if template is set
      let templateItems = [];
      if (lineProduction?.template_id) {
        try {
          // Fetch template consumption data (caps first, then bottles, labels, supplies)
          const [capsResult, bottlesResult, labelsResult, suppliesResult] = await Promise.all([
            supabase.from('template_caps_consumption').select('*, cap:cap_id(*)').eq('template_id', lineProduction.template_id),
            supabase.from('template_bottles_consumption').select('*, bottle:bottle_id(*)').eq('template_id', lineProduction.template_id),
            supabase.from('template_labels_consumption').select('*, label:label_id(*)').eq('template_id', lineProduction.template_id),
            supabase.from('template_supplies_consumption').select('*, supply:supply_id(*)').eq('template_id', lineProduction.template_id)
          ]);

          // Create items from template data (caps first, then bottles)
          if (capsResult.data && capsResult.data.length > 0) {
            capsResult.data.forEach(item => {
              templateItems.push({
                line_production_id: lineProduction.id,
                item_type: 'cap',
                item_id: item.cap_id,
                planned_quantity: totalUnitsNeeded * (item.quantity_per_unit || 1),
                planned_unit: 'units',
                added_by: user?.id,
                added_at: new Date().toISOString()
              });
            });
          }

          if (bottlesResult.data && bottlesResult.data.length > 0) {
            bottlesResult.data.forEach(item => {
              templateItems.push({
                line_production_id: lineProduction.id,
                item_type: 'bottle',
                item_id: item.bottle_id,
                planned_quantity: totalUnitsNeeded * (item.quantity_per_unit || 1),
                planned_unit: 'units',
                added_by: user?.id,
                added_at: new Date().toISOString()
              });
            });
          }

          if (labelsResult.data && labelsResult.data.length > 0) {
            labelsResult.data.forEach(item => {
              templateItems.push({
                line_production_id: lineProduction.id,
                item_type: 'label',
                item_id: item.label_id,
                planned_quantity: totalUnitsNeeded * (item.quantity_per_unit || 1),
                planned_unit: 'units',
                added_by: user?.id,
                added_at: new Date().toISOString()
              });
            });
          }

          if (suppliesResult.data && suppliesResult.data.length > 0) {
            suppliesResult.data.forEach(item => {
              templateItems.push({
                line_production_id: lineProduction.id,
                item_type: 'supply',
                item_id: item.supply_id,
                planned_quantity: totalUnitsNeeded * (item.quantity_per_unit || 1),
                planned_unit: item.supply?.unit || 'units',
                added_by: user?.id,
                added_at: new Date().toISOString()
              });
            });
          }
        } catch (templateError) {
          console.error('Error loading template data:', templateError);
        }
      }

      // If no template items found, don't create any items
      if (templateItems.length === 0) {
        await loadLineItems();
        showSnackbar('Template has no components configured. No items created.', 'info');
        setLoading(false);
        return;
      }

      const { error } = await supabase
        .from('production_line_items')
        .insert(templateItems);

      if (error) throw error;

      await loadLineItems();
      showSnackbar(`Line items generated successfully! Created ${templateItems.length} items for ${totalUnitsNeeded} units`, 'success');
      onUpdate();
    } catch (error) {
      console.error('Error generating line items:', error);
      showSnackbar('Error generating line items: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConditionClick = (event, item) => {
    setConditionMenuAnchor(event.currentTarget);
    setSelectedItemForCondition(item);
  };

  const handleConditionClose = () => {
    setConditionMenuAnchor(null);
    setSelectedItemForCondition(null);
  };

  const handleConditionChange = async (newCondition) => {
    if (!selectedItemForCondition) return;

    // Check permissions before proceeding
    if (!canEdit) {
      showSnackbar('You do not have permission to update item conditions.', 'error');
      handleConditionClose();
      return;
    }

    try {
      const { error } = await supabase
        .from('production_line_items')
        .update({ item_condition: newCondition })
        .eq('id', selectedItemForCondition.id);

      if (error) throw error;
      
      await loadLineItems();
      showSnackbar(`Condition updated to ${newCondition}`, 'success');
    } catch (error) {
      console.error('Error updating condition:', error);
      showSnackbar('Error updating condition: ' + error.message, 'error');
    } finally {
      handleConditionClose();
    }
  };

  const handleNotesClick = (event, item) => {
    setNotesEditAnchor(event.currentTarget);
    setSelectedItemForNotes(item);
    setQuickEditNotes(item.usage_notes || '');
  };

  const handleNotesEditClose = () => {
    setNotesEditAnchor(null);
    setSelectedItemForNotes(null);
    setQuickEditNotes('');
  };

  const handleQuickNotesSave = async () => {
    if (!selectedItemForNotes || savingNotes) return;

    // Check permissions before proceeding
    if (!canEdit) {
      showSnackbar('You do not have permission to update item notes.', 'error');
      handleNotesEditClose();
      return;
    }

    setSavingNotes(true);
    try {
      const updateData = {
        usage_notes: quickEditNotes || null
      };

      const { error } = await supabase
        .from('production_line_items')
        .update(updateData)
        .eq('id', selectedItemForNotes.id);

      if (error) throw error;

      await loadLineItems();
      showSnackbar('Usage notes updated successfully!', 'success');
      
    } catch (error) {
      console.error('Error updating notes:', error);
      showSnackbar('Error updating notes: ' + error.message, 'error');
    } finally {
      setSavingNotes(false);
      handleNotesEditClose();
    }
  };

  const getInventoryItemsForItem = (itemType, itemId, locationId = null) => {
    let filteredItems = inventoryItemsData.filter(invItem => {
      if (itemType === 'bottle') return invItem.bottle_id === itemId;
      if (itemType === 'cap') return invItem.cap_id === itemId;
      if (itemType === 'label') return invItem.label_id === itemId;
      if (itemType === 'supply') return invItem.supply_id === itemId;
      return false;
    });
    
    // Further filter by production location if specified
    if (lineProduction?.production_location || locationId) {
      const targetLocation = locationId || lineProduction.production_location;
      const isLocationId = targetLocation.includes('-') && targetLocation.length > 20;
      
      filteredItems = filteredItems.filter(item => {
        if (isLocationId) {
          return item.location?.id === targetLocation;
        } else {
          return item.location?.name === targetLocation;
        }
      });
    }
    
    // Sort by created date
    return filteredItems.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  };

  const getInventoryTableData = (type) => {
    const cfg = inventoryTypeConfigs[type];
    const itemIdField = cfg.itemIdField;

    const typeAggregates = inventoryData.filter(inv => {
      if (inv.inventory_type !== type) return false;
      if (lineProduction?.production_location) {
        const targetLocation = lineProduction.production_location;
        const isLocationId = targetLocation.length > 20 && targetLocation.includes('-');
        if (isLocationId) {
          return inv.location?.id === targetLocation || inv.location_id === targetLocation;
        } else {
          return inv.location?.name === targetLocation;
        }
      }
      return true;
    });

    return typeAggregates.map(inv => {
      const itemObj = inv[type];
      const lots = inventoryItemsData.filter(lot => {
        if (lot.inventory_type !== type) return false;
        if (lot[itemIdField] !== inv[itemIdField]) return false;
        if (inv.location_id && lot.location_id !== inv.location_id) return false;
        return true;
      });
      return {
        item_id: inv[itemIdField],
        item_name: itemObj?.name || 'Unknown',
        item_sku: itemObj?.sku,
        total_quantity: inv.current_quantity || 0,
        location_id: inv.location_id,
        location_name: inv.location?.name || productionLocationName || 'Unknown',
        reorder_point: inv.reorder_point || 0,
        reorder_quantity: inv.reorder_quantity || 0,
        company_id: inv.company_id,
        lots
      };
    });
  };

  const loadAvailableLotsForItem = (item) => {
    const inventoryItems = getInventoryItemsForItem(item.item_type, item.item_id);
    return inventoryItems.filter(invItem => invItem.current_quantity > 0);
  };

  const handleLotBatchAmountClick = async (item) => {
    const rawLotsForItem = loadAvailableLotsForItem(item);
    
    if (rawLotsForItem.length === 0) {
      showSnackbar('No available lots/batches found for this item', 'warning');
      return;
    }

    const lotsWithAvailability = rawLotsForItem.map(lot => ({
      ...lot,
      available_quantity_after_allocations: lot.current_quantity || 0,
      original_total_quantity: lot.current_quantity
    }));

    if (lotsWithAvailability.length === 0) {
      showSnackbar('No lots/batches have available quantity', 'warning');
      return;
    }

    setSelectedItemForLotBatch(item);
    setAvailableLots(lotsWithAvailability);
    
    // Load existing lot selections from database
    const existingSelections = await loadExistingLotSelections(item.id);
    setSelectedLots(existingSelections);
    
    // Calculate remaining quantity needed
    const plannedAmount = item.planned_quantity || 0;
    const currentTotalSelected = existingSelections.reduce((sum, sel) => sum + (sel.quantity_used || 0), 0);
    setRemainingQuantityNeeded(Math.max(0, plannedAmount - currentTotalSelected));
    
    setLotSearchTerm('');
    setShowLotBatchDialog(true);
  };

  const loadExistingLotSelections = async (itemId) => {
    try {
      const { data, error } = await supabase
        .from('production_line_item_lots')
        .select('*')
        .eq('line_item_id', itemId);

      if (error) throw error;
      
      return (data || []).map(lot => ({
        inventory_item_id: lot.inventory_item_id,
        lot_number: lot.lot_number,
        batch_number: lot.batch_number,
        quantity_used: lot.quantity_used,
        unit: lot.unit_used,
        available_total_quantity: lot.quantity_used || 0,
        expiry_date: lot.expiry_date,
        location_name: lot.location_name,
        manufactured_date: lot.manufactured_date
      }));
    } catch (error) {
      console.error('Error loading existing lot selections:', error);
      return [];
    }
  };

  const selectLotForUse = (lot) => {
    // Check if already selected
    const existingIndex = selectedLots.findIndex(sel => sel.inventory_item_id === lot.id);
    if (existingIndex >= 0) {
      showSnackbar('This lot/batch is already selected', 'warning');
      return;
    }

    const availableQuantity = lot.current_quantity || 0;
    const suggestedQuantity = Math.min(remainingQuantityNeeded, availableQuantity);

    const newSelection = {
      inventory_item_id: lot.id,
      lot_number: lot.lot_number,
      batch_number: lot.batch_number,
      available_total_quantity: availableQuantity,
      quantity_used: suggestedQuantity,
      unit: selectedItemForLotBatch.planned_unit,
      location_name: lot.location?.name
    };

    setSelectedLots([...selectedLots, newSelection]);
    
    // Update remaining quantity
    const newRemaining = Math.max(0, remainingQuantityNeeded - suggestedQuantity);
    setRemainingQuantityNeeded(newRemaining);
  };

  const updateLotQuantity = (index, newQuantity) => {
    const updatedLots = [...selectedLots];
    const oldQuantity = updatedLots[index].quantity_used || 0;
    updatedLots[index].quantity_used = parseFloat(newQuantity) || 0;
    
    setSelectedLots(updatedLots);
    
    // Recalculate remaining quantity needed
    const plannedAmount = selectedItemForLotBatch?.planned_quantity || 0;
    const currentTotalSelected = updatedLots.reduce((sum, sel) => sum + (sel.quantity_used || 0), 0);
    setRemainingQuantityNeeded(Math.max(0, plannedAmount - currentTotalSelected));
  };

  const removeLotFromSelection = (index) => {
    const updatedLots = [...selectedLots];
    const removedQuantity = updatedLots[index].quantity_used || 0;
    updatedLots.splice(index, 1);
    
    setSelectedLots(updatedLots);
    
    // Update remaining quantity
    setRemainingQuantityNeeded(remainingQuantityNeeded + removedQuantity);
  };

  const saveLotBatchSelections = async () => {
    if (selectedLots.length === 0) {
      showSnackbar('Please select at least one lot/batch', 'error');
      return;
    }
    
    if (!selectedItemForLotBatch?.id) {
      showSnackbar('No item selected for lot/batch assignment', 'error');
      return;
    }

    // Check permissions before proceeding
    if (!canEdit) {
      showSnackbar('You do not have permission to update lot/batch selections.', 'error');
      closeLotBatchDialog();
      return;
    }
    
    setSavingLotSelections(true);
    try {
      const item = selectedItemForLotBatch;
      const totalQuantityUsed = selectedLots.reduce((sum, sel) => sum + (sel.quantity_used || 0), 0);
      
      // Calculate variance
      let varianceAmount = null;
      let variancePercentage = null;
      if (item.planned_quantity) {
        varianceAmount = totalQuantityUsed - item.planned_quantity;
        variancePercentage = (varianceAmount / item.planned_quantity) * 100;
      }

      // Get supplier lot/batch numbers from selections
      const supplierLotNumbers = selectedLots.map(lot => lot.lot_number).filter(Boolean);
      const supplierBatchNumbers = selectedLots.map(lot => lot.batch_number).filter(Boolean);
      
      // Update the main line item record
      const updateData = {
        actual_quantity: totalQuantityUsed,
        actual_unit: item.planned_unit,
        variance_quantity: varianceAmount,
        variance_percentage: variancePercentage,
        supplier_lot_number: supplierLotNumbers.length > 0 ? supplierLotNumbers.join(', ') : null,
        supplier_batch_number: supplierBatchNumbers.length > 0 ? supplierBatchNumbers.join(', ') : null
      };

      const { error: updateError } = await supabase
        .from('production_line_items')
        .update(updateData)
        .eq('id', item.id);

      if (updateError) {
        throw new Error(`Failed to update line item: ${updateError.message}`);
      }

      // Delete existing lot selections for this item
      const { error: deleteError } = await supabase
        .from('production_line_item_lots')
        .delete()
        .eq('line_item_id', item.id);

      if (deleteError) {
        throw new Error(`Failed to clear existing lot selections: ${deleteError.message}`);
      }

      // Insert new lot selections to database
      if (selectedLots.length > 0) {
        const validLots = selectedLots.filter(lot => 
          lot.inventory_item_id && 
          lot.quantity_used > 0 && 
          (lot.lot_number || lot.batch_number)
        );
        
        if (validLots.length === 0) {
          throw new Error('No valid lot selections to save');
        }
        
        const lotRecords = validLots.map(lot => ({
          line_item_id: item.id,
          inventory_item_id: lot.inventory_item_id,
          quantity_used: Number(lot.quantity_used),
          unit_used: lot.unit || item.planned_unit || 'units',
          lot_number: lot.lot_number || null,
          batch_number: lot.batch_number || null,
          location_name: lot.location_name || null,
          created_by: user.id
        }));

        const { error: insertError } = await supabase
          .from('production_line_item_lots')
          .insert(lotRecords);

        if (insertError) {
          throw new Error(`Failed to save lot selections: ${insertError.message}`);
        }
      }

      // Close dialog and refresh data
      setShowLotBatchDialog(false);
      await loadLineItems();
      await loadAllLotSummaries();
      
      const lotCount = selectedLots.length;
      showSnackbar(
        `Lot/batch selections saved! Used ${lotCount} lot${lotCount === 1 ? '' : 's'} for total of ${totalQuantityUsed} ${item.planned_unit}`, 
        'success'
      );
      
    } catch (error) {
      console.error('Error saving lot/batch selections:', error);
      showSnackbar('Error saving lot/batch selections: ' + error.message, 'error');
    } finally {
      setSavingLotSelections(false);
    }
  };

  const closeLotBatchDialog = () => {
    setShowLotBatchDialog(false);
    setSelectedItemForLotBatch(null);
    setAvailableLots([]);
    setSelectedLots([]);
    setRemainingQuantityNeeded(0);
  };


  const getLotBatchSummary = (itemId) => {
    return lotSummaries[itemId] || null;
  };

  const getConditionColor = (condition) => {
    const colors = {
      'Good': 'success',
      'Acceptable': 'warning', 
      'Damaged': 'error',
      'Expired': 'error'
    };
    return colors[condition] || 'default';
  };

  const formatAmount = (amount, unit) => {
    if (!amount) return 'Not set';
    return `${parseFloat(amount).toLocaleString()} ${unit}`;
  };

  const getVarianceColor = (variance) => {
    if (!variance) return 'default';
    if (Math.abs(variance) > 10) return 'error';
    if (Math.abs(variance) > 5) return 'warning';
    return 'success';
  };

  const updateAccordionState = (accordionKey, expanded) => {
    const newStates = { ...accordionStates, [accordionKey]: expanded };
    setAccordionStates(newStates);
    // Save to localStorage
    try {
      localStorage.setItem(`productionLineItems_accordions_${lineProduction?.id}`, JSON.stringify(newStates));
    } catch (error) {
      console.warn('Failed to save accordion state to localStorage:', error);
    }
  };

  const updateItemTypeAccordionState = (itemType, expanded) => {
    const newStates = { 
      ...accordionStates, 
      itemTypes: { ...accordionStates.itemTypes, [itemType]: expanded }
    };
    setAccordionStates(newStates);
    // Save to localStorage
    try {
      localStorage.setItem(`productionLineItems_accordions_${lineProduction?.id}`, JSON.stringify(newStates));
    } catch (error) {
      console.warn('Failed to save accordion state to localStorage:', error);
    }
  };

  const toggleInventoryRowExpansion = (itemId, locationId) => {
    const key = `${itemId}-${locationId}`;
    const newExpanded = new Set(expandedInventoryRows);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedInventoryRows(newExpanded);
  };

  const isInventoryRowExpanded = (itemId, locationId) => {
    return expandedInventoryRows.has(`${itemId}-${locationId}`);
  };

  // Group items by type
  const groupedItems = items.reduce((groups, item) => {
    const itemType = item.item_type || 'unknown';
    
    if (!groups[itemType]) {
      groups[itemType] = {
        name: itemType.charAt(0).toUpperCase() + itemType.slice(1),
        items: []
      };
    }
    groups[itemType].items.push(item);
    return groups;
  }, {});

  // Sort item types with custom order (caps first, then bottles, then alphabetical)
  const itemTypeOrder = { cap: 1, bottle: 2, label: 3, supply: 4 };
  const sortedItemTypes = Object.entries(groupedItems).sort(
    ([a], [b]) => {
      const orderA = itemTypeOrder[a] || 999;
      const orderB = itemTypeOrder[b] || 999;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return a.localeCompare(b);
    }
  );


  return (
    <Box sx={{ 
      p: 2,
      pl: 3,
      pr: 3,
      maxWidth: '98vw',
      width: '100%'
    }}>
      {/* Stock Availability Overview */}
      {items.length > 0 && (
        <Accordion 
          expanded={accordionStates.inventoryOverview}
          onChange={(event, expanded) => updateAccordionState('inventoryOverview', expanded)}
          elevation={0}
          sx={{
            borderRadius: '16px !important',
            mb: 4,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            overflow: 'hidden',
            '&:before': {
              display: 'none'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
            sx={{
              background: 'white',
              backdropFilter: 'blur(10px)',
              '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backdropFilter: 'blur(10px)'
              }}>
                <InventoryIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
              </Box>
              <Box sx={{ flex: 1, ml: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                  Production Items Stock Status
                  {productionLocationName && (
                    <Chip 
                      label={productionLocationName}
                      size="small"
                      sx={{ 
                        ml: 2, 
                        background: `linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        color: 'white',
                        fontWeight: 500
                      }}
                    />
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {productionLocationName 
                    ? `Inventory levels at ${productionLocationName} for line items`
                    : 'Current inventory levels for line items'
                  }
                </Typography>
              </Box>
              {loadingInventory && (
                <Box sx={{ ml: 'auto' }}>
                  <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
                </Box>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            {['cap', 'bottle', 'label', 'supply'].map(type => {
              const tableData = getInventoryTableData(type);
              if (tableData.length === 0) return null;
              const cfg = inventoryTypeConfigs[type];
              return (
                <Box key={type} sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: 1 }}>
                    {cfg.displayName}
                  </Typography>
                  <InventoryTable
                    data={tableData}
                    config={cfg}
                    inventoryType={type}
                    expandable={true}
                    onRefresh={loadInventoryData}
                    canEdit={false}
                    canDelete={false}
                  />
                </Box>
              );
            })}
            {!loadingInventory && ['cap', 'bottle', 'label', 'supply'].every(type => getInventoryTableData(type).length === 0) && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No inventory found at the selected production location for the template items.
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      <Grid container spacing={3}>
        {/* Panel A: Item Summary */}
        <Grid item xs={12} lg={3.5}>
          <Card sx={{ 
            height: 'fit-content',
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: `white`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
        <CardContent sx={{ p: 4 }}>
              <Stack spacing={3}>
                {/* Enhanced Header */}
                <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              backdropFilter: 'blur(10px)'
            }}>
              <InventoryIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
            </Box>
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
              Production Line Items
            </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Item tracking
            </Typography>
                    </Box>
          </Box>
                </Box>

                {/* Generate Items Button */}
                {items.length === 0 && canEdit && (
                  <Button
                    fullWidth
                    variant="outlined"
                    size="medium"
                    startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
                    disabled={loading}
                    onClick={generateLineItems}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      px: 3,
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                      background: theme.palette.grey[400],
                      border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        backgroundColor: theme.palette.grey[400]
                      },
                      '&:disabled': {
                        background: theme.palette.grey[300],
                        color: theme.palette.grey[100]
                      }
                    }}
                  >
                    {loading ? 'Generating Items...' : 'Generate Items'}
                  </Button>
                )}

                {/* Quick Stats */}
                {items.length > 0 && (
                  <Accordion 
                    expanded={accordionStates.progressOverview}
                    onChange={(event, expanded) => updateAccordionState('progressOverview', expanded)}
                    elevation={0}
                    sx={{
                      borderRadius: '12px !important',
                      border: `1px solid ${theme.palette.grey[400]}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      overflow: 'hidden',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:before': {
                        display: 'none'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      }
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                      sx={{
                        background: 'white',
                        backdropFilter: 'blur(10px)',
                        '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 }
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <VarianceIcon sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                          Progress Overview
          </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        {/* Overall Progress */}
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Completion Rate
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 500, color: '#000000' }}>
                            {Math.round((items.filter(i => i.actual_quantity).length / items.length) * 100)}%
                          </Typography>
                        </Box>

                        <Divider />

                        {/* Type Breakdown */}
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            By Type
                          </Typography>
                          <Stack spacing={1}>
                            {sortedItemTypes.map(([itemType, typeData]) => {
                              const completed = typeData.items.filter(i => i.actual_quantity).length;
                              const total = typeData.items.length;
                              const percentage = total > 0 ? (completed / total) * 100 : 0;
                              
                              return (
                                <Box key={itemType} sx={{ 
                                  p: 2,
                                  borderRadius: 3,
                                  border: `1px solid ${theme.palette.grey[400]}`,
                                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                  backdropFilter: 'blur(10px)',
                                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                                  }
                                }}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                      {typeData.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: percentage === 100 ? '#2e7d32' : '#000000' }}>
                                      {completed}/{total}
                                    </Typography>
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Stack>
        </CardContent>
      </Card>
        </Grid>

        {/* Panel B: Items by Type */}
        <Grid item xs={12} lg={5.5}>
          <Card sx={{ 
            height: 'fit-content',
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: `white`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 3 }}>
                Production Items
              </Typography>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: '#000000' }} />
                </Box>
              ) : items.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <InventoryIcon sx={{ fontSize: 48, color: '#666666', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No Items Generated
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Generate items for the production line to start tracking.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {sortedItemTypes.map(([itemType, typeData]) => (
                    <Accordion 
                      key={itemType}
                      expanded={accordionStates.itemTypes[itemType] || false}
                      onChange={(event, expanded) => updateItemTypeAccordionState(itemType, expanded)}
                      elevation={0}
                      sx={{
                        borderRadius: '12px !important',
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        backdropFilter: 'blur(20px)',
                        overflow: 'hidden',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                        '&:before': {
                          display: 'none'
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                        }
                      }}
                    >
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                        sx={{
                          background: 'white',
                          backdropFilter: 'blur(10px)',
                          minHeight: 56,
                          '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 }
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                            {typeData.name}
                          </Typography>
                          <Chip 
                            label={`${typeData.items.length} item${typeData.items.length === 1 ? '' : 's'}`}
                            size="medium"
                            sx={{ 
                              fontSize: '0.85rem',
                              fontWeight: 400,
                              height: 28,
                              bgcolor: theme.palette.grey[300],
                              color: theme.palette.primary.main
                            }}
                          />
                          <Box sx={{ flex: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            {typeData.items.filter(i => i.actual_quantity).length}/{typeData.items.length}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                          {typeData.items.map((item) => (
                            <Box
                              key={item.id}
                              sx={{ 
                                p: 3,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.grey[400]}`,
                                background: `white`,
                                backdropFilter: 'blur(20px)',
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                                }
                              }}
                            >
                              {/* Item Header */}
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000', mb: 0.5 }}>
                                  {(() => {
                                    if (item.item_type === 'bottle' && item.bottle) {
                                      return item.bottle.name;
                                    } else if (item.item_type === 'cap' && item.cap) {
                                      return item.cap.name;
                                    } else if (item.item_type === 'label' && item.label) {
                                      return item.label.name;
                                    } else if (item.item_type === 'supply' && item.supply) {
                                      return item.supply.name;
                                    } else {
                                      return `${item.item_type.charAt(0).toUpperCase() + item.item_type.slice(1)} Item`;
                                    }
                                  })()}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Planned: {formatAmount(item.planned_quantity, item.planned_unit)}
                                  </Typography>
                                  {(() => {
                                    if (item.item_type === 'bottle' && item.bottle?.sku) {
                                      return <Typography variant="caption" color="text.secondary">SKU: {item.bottle.sku}</Typography>;
                                    } else if (item.item_type === 'cap' && item.cap?.sku) {
                                      return <Typography variant="caption" color="text.secondary">SKU: {item.cap.sku}</Typography>;
                                    } else if (item.item_type === 'label' && item.label?.sku) {
                                      return <Typography variant="caption" color="text.secondary">SKU: {item.label.sku}</Typography>;
                                    } else if (item.item_type === 'supply' && item.supply?.sku) {
                                      return <Typography variant="caption" color="text.secondary">SKU: {item.supply.sku}</Typography>;
                                    }
                                    return null;
                                  })()}
                                </Stack>
                              </Box>

                              {/* Actual Amount */}
                              <Box
                                onClick={() => canEdit ? handleLotBatchAmountClick(item) : showSnackbar('You do not have permission to edit lot/batch selections.', 'error')}
                                sx={{ 
                                  p: 2,
                                  borderRadius: 3,
                                  border: `1px solid ${item.actual_quantity ? theme.palette.success.main : theme.palette.warning.main}`,
                                  cursor: 'pointer',
                                  background: item.actual_quantity ? 
                                    `linear-gradient(${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})` : 
                                    `linear-gradient(${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
                                  backdropFilter: 'blur(10px)',
                                  mb: 1.5,
                                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                                  }
                                }}
                              >
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  {(() => {
                                    const lotSummary = getLotBatchSummary(item.id);
                                    
                                    if (lotSummary) {
                                      return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                          <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                              Actual: {formatAmount(lotSummary.totalQuantity, item.planned_unit)}
                                            </Typography>
                                            <Chip 
                                              label={`${lotSummary.lotCount} lot${lotSummary.lotCount === 1 ? '' : 's'}`}
                                              size="small"
                                              sx={{ 
                                                height: '20px', 
                                                fontSize: '0.7rem',
                                                bgcolor: theme.palette.grey[300],
                                                color: theme.palette.primary.main
                                              }}
                                            />
                                          </Stack>
                                        </Box>
                                      );
                                    } else {
                                      return (
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                          Click to select lot/batch
                                        </Typography>
                                      );
                                    }
                                  })()}
                                  
                                  {item.variance_percentage && Math.abs(item.variance_percentage) > 0.01 && (
                                    <Chip 
                                      label={`${item.variance_percentage > 0 ? '+' : ''}${item.variance_percentage.toFixed(1)}%`}
                                      color={getVarianceColor(item.variance_percentage)}
                                      size="small"
                                    />
                                  )}
                                </Stack>
                              </Box>

                              {/* Show lot/batch info if selected */}
                              {(() => {
                                const lotSummary = getLotBatchSummary(item.id);
                                return lotSummary && lotSummary.lots.length > 0 ? (
                                  <Box sx={{ 
                                    p: 2,
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.success.main}`,
                                    background: `linear-gradient(${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
                                    backdropFilter: 'blur(10px)',
                                    mb: 1.5,
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                                    }
                                  }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: '#2e7d32', display: 'block', mb: 1 }}>
                                      Selected Lots/Batches:
                                    </Typography>
                                    <Stack spacing={0.5}>
                                      {lotSummary.lots.map((lot, index) => (
                                        <Typography key={index} variant="caption" color="text.secondary">
                                          • {lot.lot_number || lot.batch_number} ({lot.quantity_used} {lot.unit})
                                        </Typography>
                                      ))}
                                    </Stack>
                                  </Box>
                                ) : null;
                              })()}

                              {/* Quick Actions */}
                              <Stack spacing={1.5}>
                                {/* Action Row - Notes & Quality */}
                                <Stack direction="row" spacing={1}>
                                  <Tooltip title={item.usage_notes || 'Add usage notes'}>
                                    <Button
                                      size="medium"
                                      onClick={(e) => canEdit ? handleNotesClick(e, item) : showSnackbar('You do not have permission to edit item notes.', 'error')}
                                      sx={{
                                        flex: 1,
                                        color: theme.palette.primary.main,
                                        fontWeight: 500,
                                        background: theme.palette.grey[300],
                                        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                        borderRadius: 2,
                                        backdropFilter: 'blur(10px)',
                                        fontSize: '0.8rem',
                                        py: 1,
                                        px: 1.5,
                                        minHeight: '40px',
                                        textTransform: 'none',
                                        '&:hover': {
                                          background: theme.palette.grey[400]
                                        }
                                      }}
                                    >
                                      {item.usage_notes 
                                        ? item.usage_notes.length > 18 
                                          ? `${item.usage_notes.substring(0, 18)}...`
                                          : item.usage_notes
                                        : 'Notes'
                                      }
                                    </Button>
                                  </Tooltip>
                                  <Button
                                    size="medium"
                                    onClick={(e) => canEdit ? handleConditionClick(e, item) : showSnackbar('You do not have permission to update item conditions.', 'error')}
                                    variant={item.item_condition ? "contained" : "outlined"}
                                    sx={{
                                      flex: 1,
                                      bgcolor: item.item_condition ? getConditionColor(item.item_condition) === 'success' ? '#4caf50' : 
                                             getConditionColor(item.item_condition) === 'warning' ? '#ff9800' : '#f44336' : 'transparent', borderRadius: 2,
                                      color: item.item_condition ? '#ffffff' : '#000000',
                                      borderColor: '#000000',
                                      fontSize: '0.8rem',
                                      py: 1,
                                      px: 1.5,
                                      minHeight: '40px',
                                      textTransform: 'none'
                                    }}
                                  >
                                    {item.item_condition ? `Quality: ${item.item_condition}` : 'Set Quality'}
                                  </Button>
                                </Stack>
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Panel C: Analytics & Progress */}
        <Grid item xs={12} lg={3}>
          <Stack spacing={3}>
            {/* Quick Actions Hub */}
            <Card sx={{ 
              borderRadius: 4,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: `white`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                  Quick Actions Hub
                </Typography>
                
                <Stack spacing={2}>
                  {/* Mass Actions */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Item Operations
                    </Typography>
                    <Stack spacing={1}>
                      {canEdit && (
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          disabled={loading}
                          onClick={async () => {
                          if (items.length === 0) {
                            showSnackbar('No items to update', 'info');
                            return;
                          }
                          
                          setLoading(true);
                          try {
                            const toUpdate = items;
                            
                            for (let i = 0; i < toUpdate.length; i += 10) {
                              const batch = toUpdate.slice(i, i + 10);
                              const updates = batch.map(item => 
                                supabase
                                  .from('production_line_items')
                                  .update({ item_condition: 'Good' })
                                  .eq('id', item.id)
                              );
                              await Promise.all(updates);
                            }
                            
                            await loadLineItems();
                            showSnackbar(`${toUpdate.length} items set to Good condition`, 'success');
                          } catch (error) {
                            console.error('Error updating conditions:', error);
                            showSnackbar('Error updating conditions: ' + error.message, 'error');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        sx={{
                          borderColor: '#4caf50',
                          color: '#4caf50',
                          borderRadius: 2,
                          '&:hover': { borderColor: '#388e3c', bgcolor: '#f1f8e9' }
                        }}
                        >
                          {loading ? 'Updating...' : 'Mark All Items as Good'}
                        </Button>
                      )}
                    </Stack>
                  </Box>

                  <Divider />

                  {/* Production Notes */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Production Notes
                    </Typography>
                    <Stack spacing={1}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        size="small"
                        label="Line Notes"
                        placeholder="Enter production notes, observations, issues, or special handling instructions..."
                        value={productionNotes}
                        onChange={(e) => setProductionNotes(e.target.value)}
                        disabled={!canEdit}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                      {canEdit && (
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          startIcon={savingProductionNotes ? <CircularProgress size={16} /> : <SaveIcon />}
                          disabled={savingProductionNotes || !productionNotes.trim()}
                          onClick={async () => {
                          if (!productionNotes.trim()) return;
                          
                          setSavingProductionNotes(true);
                          try {
                            const { error } = await supabase
                              .from('production_line_notes')
                              .insert({
                                line_production_id: lineProduction.id,
                                note_content: productionNotes,
                                note_type: 'general',
                                created_by: user.id
                              });
                            
                            if (error) throw error;
                            
                            await loadLineNotes();
                            showSnackbar('Production notes saved successfully!', 'success');
                            setProductionNotes('');
                          } catch (error) {
                            console.error('Error saving notes:', error);
                            showSnackbar('Error saving notes: ' + error.message, 'error');
                          } finally {
                            setSavingProductionNotes(false);
                          }
                        }}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          color: theme.palette.text.secondary,
                          fontWeight: 500,
                          background: 'rgba(255, 255, 255, 0.95)',
                          border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                          backdropFilter: 'blur(10px)'
                        }}
                        >
                          {savingProductionNotes ? 'Saving...' : 'Save Notes'}
                        </Button>
                      )}
                      
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={loadingLineNotes ? <CircularProgress size={16} /> : <NotesIcon />}
                        disabled={loadingLineNotes}
                        onClick={() => {
                          if (lineNotes.length > 0) {
                            setShowSavedNotesDialog(true);
                          } else {
                            showSnackbar('No saved notes for this line run yet', 'info');
                          }
                        }}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          color: theme.palette.text.secondary,
                          fontWeight: 500,
                          background: 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)'
                        }}
                      >
                        {loadingLineNotes ? 'Loading...' : `View Notes (${lineNotes.length})`}
                      </Button>
                    </Stack>
                  </Box>

                  <Divider />

                  {/* System Actions */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      System Actions
                    </Typography>
                    <Stack spacing={1}>
                      <Button
                        fullWidth
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={async () => {
                          await loadLineItems(true);
                          loadInventoryData();
                        }}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          color: theme.palette.primary.main,
                          fontWeight: 500,
                          background: theme.palette.grey[400],
                          border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                          backdropFilter: 'blur(10px)',
                          '&:hover': {
                            backgroundColor: theme.palette.grey[400]
                          }
                        }}
                      >
                        Refresh All Data
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={() => {
                          if (!canEdit) {
                            showSnackbar('You do not have permission to save progress.', 'error');
                            return;
                          }
                          if (onUpdate) onUpdate();
                          showSnackbar('Progress saved successfully!', 'success');
                        }}
                        disabled={!canEdit}
                        sx={{
                          borderColor: canEdit ? '#000000' : theme.palette.grey[400],
                          borderRadius: 2,
                          color: canEdit ? '#000000' : theme.palette.grey[500],
                          '&:hover': canEdit ? { 
                            borderColor: '#000000', 
                            bgcolor: '#f5f5f5'
                          } : {},
                          '&:disabled': {
                            borderColor: theme.palette.grey[400],
                            color: theme.palette.grey[500],
                            cursor: 'not-allowed'
                          }
                        }}
                      >
                        Save Progress
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Quick Condition Change Menu */}
      <Menu
        anchorEl={conditionMenuAnchor}
        open={Boolean(conditionMenuAnchor)}
        onClose={handleConditionClose}
      >
        <MenuItem onClick={() => handleConditionChange('Good')}>
          <Chip label="Good" color="success" size="small" sx={{ mr: 1 }} />
          Good
        </MenuItem>
        <MenuItem onClick={() => handleConditionChange('Acceptable')}>
          <Chip label="Acceptable" color="warning" size="small" sx={{ mr: 1 }} />
          Acceptable
        </MenuItem>
        <MenuItem onClick={() => handleConditionChange('Damaged')}>
          <Chip label="Damaged" color="error" size="small" sx={{ mr: 1 }} />
          Damaged
        </MenuItem>
        <MenuItem onClick={() => handleConditionChange('Expired')}>
          <Chip label="Expired" color="error" size="small" sx={{ mr: 1 }} />
          Expired
        </MenuItem>
      </Menu>

      {/* Quick Notes Edit Menu */}
      <Menu
        anchorEl={notesEditAnchor}
        open={Boolean(notesEditAnchor)}
        onClose={handleNotesEditClose}
        PaperProps={{
          sx: { 
            p: 2, 
            minWidth: 320,
            maxWidth: 400
          }
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
            Set Usage Notes
          </Typography>
          {selectedItemForNotes && (
            <Typography variant="caption" color="text.secondary">
              {selectedItemForNotes.item_type.charAt(0).toUpperCase() + selectedItemForNotes.item_type.slice(1)} Item
            </Typography>
          )}
          </Box>
          
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Usage Notes"
            multiline
            rows={3}
            value={quickEditNotes}
            onChange={(e) => setQuickEditNotes(e.target.value)}
            size="small"
            placeholder="Enter any notes about item usage, quality, or special handling..."
            disabled={!canEdit}
          />
          
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleNotesEditClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              size="small" 
              onClick={handleQuickNotesSave}
              disabled={savingNotes}
              fullWidth
              startIcon={savingNotes ? <CircularProgress size={16} /> : null}
            >
              {savingNotes ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </Menu>

      {/* Lot/Batch Selection Dialog */}
      <Dialog
        open={showLotBatchDialog}
        onClose={closeLotBatchDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
          mb: 6
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <LotIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main', mb: 0.5 }}>
                Select Lot/Batch for {selectedItemForLotBatch?.item_type?.charAt(0).toUpperCase() + selectedItemForLotBatch?.item_type?.slice(1)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Choose specific lots/batches to use for this item
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ 
          backgroundColor: alpha(theme.palette.grey[50], 0.5), 
          p: '80px 24px 24px 24px',
          backdropFilter: 'blur(10px)'
        }}>
          <Stack spacing={4}>
            {/* Summary Info */}
            <Box sx={{ 
              p: 2, 
              bgcolor: alpha(theme.palette.primary.main, 0.04), 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              backdropFilter: 'blur(10px)'
            }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Planned Amount:</Typography>
                  <Typography variant="h6">
                    {(selectedItemForLotBatch?.planned_quantity || 0).toLocaleString()} {selectedItemForLotBatch?.planned_unit}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Remaining Needed:</Typography>
                  <Typography 
                    variant="h6" 
                    color={remainingQuantityNeeded > 0 ? 'warning.main' : 'success.main'}
                  >
                    {remainingQuantityNeeded.toLocaleString()} {selectedItemForLotBatch?.planned_unit}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Selected Lots */}
            {selectedLots.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Selected Lots/Batches ({selectedLots.length})
                </Typography>
                <Stack spacing={1}>
                  {selectedLots.map((lot, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={3}>
                          <Typography variant="body2" color="textSecondary">
                            {lot.lot_number ? 'Lot:' : 'Batch:'}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {lot.lot_number || lot.batch_number}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography variant="body2" color="textSecondary">Available:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                            {(lot.available_total_quantity || 0).toLocaleString()} units
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="textSecondary">Using:</Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={lot.quantity_used || ''}
                            onChange={(e) => updateLotQuantity(index, e.target.value)}
                            inputProps={{
                              min: 0.1,
                              max: lot.available_total_quantity || 0,
                              step: 1
                            }}
                            error={lot.quantity_used > (lot.available_total_quantity || 0) || lot.quantity_used <= 0}
                            helperText={`Max: ${(lot.available_total_quantity || 0).toLocaleString()} units`}
                            disabled={!canEdit}
                            sx={{ 
                              mt: 0.5,
                              '& .MuiFormHelperText-root': {
                                fontSize: '0.7rem'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <Typography variant="body2" color="textSecondary">Location:</Typography>
                          <Typography variant="body2">
                            {lot.location_name || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton
                            onClick={() => removeLotFromSelection(index)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Available Lots */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6">
                  Available Lots/Batches ({availableLots.filter(lot => {
                    if (!lotSearchTerm) return true;
                    const term = lotSearchTerm.toLowerCase();
                    return (lot.lot_number || '').toLowerCase().includes(term) ||
                           (lot.batch_number || '').toLowerCase().includes(term) ||
                           (lot.location?.name || '').toLowerCase().includes(term);
                  }).length}{lotSearchTerm ? ` of ${availableLots.length}` : ''})
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    // Auto-fill lots with planned amount - preserve original order
                    const plannedAmount = selectedItemForLotBatch?.planned_quantity || 0;
                    let remainingToFill = plannedAmount;
                    const autoSelections = [];
                    
                    // Use lots in their current order without any sorting
                    for (const lot of availableLots) {
                      if (remainingToFill <= 0) break;
                      
                      const availableQuantity = lot.current_quantity || 0;
                      const quantityToUse = Math.min(remainingToFill, availableQuantity);
                      
                      if (quantityToUse > 0) {
                        autoSelections.push({
                          inventory_item_id: lot.id,
                          lot_number: lot.lot_number,
                          batch_number: lot.batch_number,
                          available_total_quantity: availableQuantity,
                          quantity_used: quantityToUse,
                          unit: selectedItemForLotBatch.planned_unit,
                          location_name: lot.location?.name
                        });
                        
                        remainingToFill -= quantityToUse;
                      }
                    }
                    
                    setSelectedLots(autoSelections);
                    setRemainingQuantityNeeded(Math.max(0, remainingToFill));
                    
                    showSnackbar(`Auto-filled ${autoSelections.length} lots for ${plannedAmount - remainingToFill} units`, 'success');
                  }}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`
                    }
                  }}
                >
                  Auto-Fill Planned Amount
                </Button>
              </Stack>

              <TextField
                placeholder="Search by lot, batch, or location..."
                size="small"
                fullWidth
                value={lotSearchTerm}
                onChange={(e) => setLotSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' }
                }}
              />

              {availableLots.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <InventoryIcon fontSize="large" color="disabled" />
                  <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
                    No available lots/batches found for this item
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `1px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>
                          Lot/Batch
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `1px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>
                          Available Qty
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `1px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>
                          Location
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `1px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {availableLots.filter(lot => {
                        if (!lotSearchTerm) return true;
                        const term = lotSearchTerm.toLowerCase();
                        return (lot.lot_number || '').toLowerCase().includes(term) ||
                               (lot.batch_number || '').toLowerCase().includes(term) ||
                               (lot.location?.name || '').toLowerCase().includes(term);
                      }).map((lot) => {
                        const isSelected = selectedLots.some(sel => sel.inventory_item_id === lot.id);
                        
                        return (
                          <TableRow 
                            key={lot.id}
                            hover
                            onClick={() => selectLotForUse(lot)}
                            sx={{
                              cursor: isSelected ? 'not-allowed' : 'pointer',
                              opacity: isSelected ? 0.7 : 1,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: isSelected ? 'inherit' : 'grey.50',
                                transform: isSelected ? 'none' : 'translateX(4px)',
                                boxShadow: isSelected ? 'none' : 'inset 4px 0 0 black'
                              },
                              '& .MuiTableCell-root': {
                                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                py: 2.5,
                                px: 3
                              }
                            }}
                          >
                            <TableCell>
                              <Box>
                                <Typography 
                                  variant="body1" 
                                  sx={{ 
                                    fontWeight: 500,
                                    fontSize: '0.95rem',
                                    color: theme.palette.text.primary,
                                    mb: 0.5
                                  }}
                                >
                                  {lot.lot_number || lot.batch_number || 'N/A'}
                                </Typography>
                                {isSelected && (
                                  <Chip 
                                    label="Selected" 
                                    size="small" 
                                    color="primary" 
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'green' }}>
                                {(lot.current_quantity || 0).toLocaleString()} units available
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">
                                {lot.location?.name || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label="In Stock"
                                color="success"
                                size="small"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={closeLotBatchDialog}
            startIcon={<CloseIcon />}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={saveLotBatchSelections}
            variant="contained"
            startIcon={savingLotSelections ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={
              selectedLots.length === 0 || 
              savingLotSelections ||
              selectedLots.some(lot => 
                (lot.quantity_used > (lot.available_total_quantity || 0)) || 
                (lot.quantity_used <= 0 || !lot.quantity_used)
              )
            }
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              '&:disabled': {
                background: theme.palette.grey[400],
                color: theme.palette.grey[100]
              }
            }}
          >
            {savingLotSelections 
              ? 'Saving...' 
              : `Save Selections (${selectedLots.length} lot${selectedLots.length === 1 ? '' : 's'})`
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Saved Notes Dialog */}
      <Dialog
        open={showSavedNotesDialog}
        onClose={() => setShowSavedNotesDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 4
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <NotesIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 500 }}>
              Saved Production Notes ({lineNotes.length})
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ 
          backgroundColor: alpha(theme.palette.grey[50], 0.5), 
          p: 3,
          backdropFilter: 'blur(10px)'
        }}>
          <Box sx={{ mt: 3 }}>
            {lineNotes.length === 0 ? (
              <Alert severity="info">No production notes available for this line run.</Alert>
            ) : (
              <Stack spacing={2}>
                {lineNotes.map((note) => (
                  <Card key={note.id} variant="outlined" sx={{ 
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    borderRadius: 3,
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    }
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      {editingNote?.id === note.id ? (
                        <Stack spacing={2}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Editing note from {new Date(note.created_at).toLocaleString()}
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={editNoteContent}
                            onChange={(e) => setEditNoteContent(e.target.value)}
                            size="small"
                            placeholder="Enter production notes..."
                            disabled={!canEdit}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { 
                                  borderColor: alpha(theme.palette.grey[400], 0.8), 
                                  borderWidth: 1,
                                  borderRadius: 2
                                },
                                '&:hover fieldset': { 
                                  borderColor: alpha(theme.palette.primary.main, 0.4), 
                                  borderWidth: 1 
                                },
                                '&.Mui-focused fieldset': { 
                                  borderColor: theme.palette.primary.main, 
                                  borderWidth: 1 
                                }
                              }
                            }}
                          />
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              onClick={cancelEditNote}
                              disabled={savingEditNote}
                              sx={{
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                background: 'rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                }
                              }}
                            >
                              Cancel
                            </Button>
                            {canEdit && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={saveEditNote}
                                disabled={savingEditNote || !editNoteContent.trim()}
                                startIcon={savingEditNote ? <CircularProgress size={16} /> : null}
                              sx={{
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                color: 'white',
                                fontWeight: 500,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                '&:hover': {
                                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                                },
                                '&:disabled': {
                                  background: theme.palette.grey[400],
                                  color: theme.palette.grey[100]
                                }
                              }}
                              >
                                {savingEditNote ? 'Saving...' : 'Save'}
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      ) : (
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {new Date(note.created_at).toLocaleString()} • {note.note_type}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {note.note_content}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            {canEdit && (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => startEditNote(note)}
                                sx={{ mt: -0.5 }}
                              >
                                <EditNoteIcon fontSize="small" />
                              </IconButton>
                            )}
                            {canDelete && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => deleteLineNote(note.id)}
                                disabled={deletingNoteId === note.id}
                                sx={{ mt: -0.5 }}
                              >
                                {deletingNoteId === note.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            )}
                          </Stack>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => setShowSavedNotesDialog(false)}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Close
          </Button>
          {lineNotes.length > 0 && canDelete && (
            <Button
              onClick={deleteAllLineNotes}
              variant="contained"
              startIcon={loadingLineNotes ? <CircularProgress size={16} /> : <DeleteIcon />}
              disabled={loadingLineNotes}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: '#d32f2f',
                color: 'white',
                fontWeight: 500,
                '&:hover': {
                  background: '#b71c1c'
                },
                '&:disabled': {
                  background: theme.palette.grey[400],
                  color: theme.palette.grey[100]
                }
              }}
            >
              {loadingLineNotes ? 'Deleting...' : 'Delete All Notes'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductionLineItems;